import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getNursingAlerts,
  getPostpartumAlerts,
  getStoreAlerts,
  getHomeAlerts
} from '@/service/alerts';

// ==================== 初始状态 ====================

const defaultState = {
  // 全局状态（向后兼容，但建议使用按 serviceType 的状态）
  fetching: false,
  error: null,
  query: {},
  data: {
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  // 按 serviceType 存储数据，每个 serviceType 有独立的 fetching 和 error 状态
  nursing: { // 护理预警
    fetching: false,
    error: null,
    query: {},
    list: [],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  homeService: { // 到家预警
    fetching: false,
    error: null,
    query: {},
    list: [
      { alert_level: 4, alert_type: "宝宝发烧", create_time: 1764817856, customer_name: "张小明", id: 2611, member_id: 485165, related_business_id: 56348, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 2, alert_type: "二阳, 感冒", create_time: 1764817856, customer_name: "邓冰清", id: 2609, member_id: 405248, related_business_id: 48166, status: "跟进中", updated_at: 1765160241},
      { alert_level: 1, alert_type: "感冒", create_time: 1764817856, customer_name: "美玲", id: 2613, member_id: 485162, related_business_id: 56343, status: "跟进中", updated_at: 176516024 },

    ],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  postpartumService: { // 产康预警
    fetching: false,
    error: null,
    query: {},
    list: [
      { alert_level: 3, alert_type: "客户投诉", create_time: 1764817856, customer_name: "邓小明", id: 2811, member_id: 488165, related_business_id: 56398, status: "跟进中", updated_at: 1765160241 },

    ],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  roomService: { // 客房预警
    fetching: false,
    error: null,
    query: {},
    list: [
      { alert_level: 4, alert_type: "超时未打扫", create_time: 1764817856, customer_name: "506", id: 2111, member_id: 475165, related_business_id: 56248, status: "跟进中", updated_at: 1765160241 },

    ],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  cateringService: { // 餐饮预警
    fetching: false,
    error: null,
    query: {},
    list: [
      { alert_level: 4, alert_type: "超时未配餐", create_time: 1764817856, customer_name: "罗小明", id: 6611, member_id: 885165, related_business_id: 96348, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 2, alert_type: "503未配餐", create_time: 1764817856, customer_name: "林小明", id: 6511, member_id: 885565, related_business_id: 96548, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 4, alert_type: "超时未配餐", create_time: 1764817856, customer_name: "罗小明", id: 6411, member_id: 885165, related_business_id: 96348, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 2, alert_type: "503未配餐", create_time: 1764817856, customer_name: "林小明", id: 6811, member_id: 885565, related_business_id: 96548, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 4, alert_type: "超时未配餐", create_time: 1764817856, customer_name: "罗小明", id: 6111, member_id: 885165, related_business_id: 96348, status: "跟进中", updated_at: 1765160241 },
      { alert_level: 2, alert_type: "503未配餐", create_time: 1764817856, customer_name: "林小明", id: 6521, member_id: 885565, related_business_id: 96548, status: "跟进中", updated_at: 1765160241 },

    ],
    total: 0,
    pageNumber: 1,
    pageSize: 20
  },
  monitoringService: {}
}

// ==================== 服务类型映射配置 ====================

/**
 * 服务类型映射函数 - 根据serviceType调用不同的API服务
 * 实现灵活的多服务支持，便于扩展新的API接口
 * @param {string} serviceType - 服务类型标识
 * @param {Object} query - 查询参数
 * @returns {Function} 返回对应的API调用函数
 */
const switchPayload = (serviceType, query) => {
  const payload = {
    nursing: () => getNursingAlerts(query), // 护理预警列表
    postpartumService: () => getPostpartumAlerts(query), // 产康预警列表
    storeAlerts: () => getStoreAlerts(query), // 店务预警列表
    homeService: () => getHomeAlerts(query), // 到家预警列表
    undefined: () => console.error('Redux: fetchAlerts 请求未定义 serviceType 类型')
  };
  return payload[serviceType] || payload.undefined;
};

// ==================== 异步Action ====================

/**
 * 异步获取预警列表的thunk action
 * 支持多服务类型
 * @param {Object} options - 请求选项
 * @param {string} options.serviceType - 服务类型，必填 (nursing, store, home_service)
 * @param {Object} options.query - 查询参数对象
 * @returns {Promise} 返回处理后的API响应数据
 */
export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async ({ serviceType, query = {} }, { rejectWithValue }) => {
    try {
      if (!serviceType) {
        return rejectWithValue('serviceType 参数是必需的');
      }
      const response = await switchPayload(serviceType, query)();
      return {
        data: response.data,
        serviceType,
        query
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || `获取${serviceType}预警列表失败`);
    }
  }
);

// ==================== Redux Slice ====================

const alertsSlice = createSlice({
  name: 'alerts',
  initialState: defaultState,
  reducers: {
    resetAlertsState: (state, action) => {
      const serviceType = action.payload?.serviceType;
      state[serviceType] = {
        fetching: false,
        error: null,
        query: {},
        list: [],
        total: 0,
        pageNumber: 1,
        pageSize: 20
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state, action) => {
        const { serviceType, query } = action.meta.arg;
        
        // 更新全局状态（向后兼容）
        state.fetching = true;
        state.error = null;
        state.query = query;
        
        // 更新对应 serviceType 的状态
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = true;
          state[serviceType].error = null;
          state[serviceType].query = query;
        }
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        const { data, serviceType, query } = action.payload;
        
        // 更新全局状态（向后兼容，使用最后一个完成的请求）
        state.fetching = false;
        state.error = null;
        state.data = data || {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        };
        state.query = query;
        
        // 按 serviceType 存储数据，每个 serviceType 独立管理状态
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = false;
          state[serviceType].error = null;
          state[serviceType].query = query;
          // 合并数据，保留 list, total, pageNumber, pageSize 等字段
          if (data) {
            state[serviceType].list = data.list || [];
            state[serviceType].total = data.total || 0;
            state[serviceType].pageNumber = data.pageNumber || 1;
            state[serviceType].pageSize = data.pageSize || 20;
            // 如果 data 中有其他字段，也保留
            Object.keys(data).forEach(key => {
              if (!['list', 'total', 'pageNumber', 'pageSize'].includes(key)) {
                state[serviceType][key] = data[key];
              }
            });
          }
        }
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        const { serviceType, query } = action.meta.arg;
        const error = action.payload;
        
        // 更新全局状态（向后兼容）
        state.fetching = false;
        state.error = error;
        state.query = query || {};
        state.data = {
          list: [],
          total: 0,
          pageNumber: 1,
          pageSize: 20
        };
        
        // 更新对应 serviceType 的状态
        if (serviceType && state[serviceType]) {
          state[serviceType].fetching = false;
          state[serviceType].error = error;
          state[serviceType].query = query || {};
          state[serviceType].list = [];
          state[serviceType].total = 0;
          state[serviceType].pageNumber = 1;
          state[serviceType].pageSize = 20;
        }
      });
  }
})

export const { resetAlertsState } = alertsSlice.actions;
export default alertsSlice.reducer;
